/**
 * The doors of Acceso that are not a form: «Confirmando tu sesión» (the Exylon mark breathing on the orbs, no
 * skeleton), «Todavía no tienes un puesto asignado» and «No se ha podido abrir el Panel». Same sentences and the same
 * two actions each; «Volver a comprobar» and «Reintentar» say «Comprobando…» in place while the Service answers.
 */

import { AnimatePresence } from './living/presence';
import { m } from './living/elements';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { AccesoDoor, useDoorChecking, useDoorMark } from './acceso-door';
import { ActionLabel, ExylonMark, Row, useDoorStill } from './acceso-parts';
import { SPRING, useLivingStill } from './living/motion-provider';

/** After three seconds the words appear under the mark, so a slow check never looks frozen. */
export function AccesoRestoring() {
  return <AccesoDoor view="restoring"><RestoringMark /></AccesoDoor>;
}

function RestoringMark() {
  const still = useDoorStill();
  const { markRef, hidden } = useDoorMark();
  // Standing still (a capture, reduced motion), the words are simply there.
  const standingStill = useLivingStill();
  const [slow, setSlow] = useState(standingStill);
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 3000);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <section aria-busy="true" aria-label="Confirmando tu sesión" className="acc-restoring">
      <ExylonMark breathing hidden={hidden} {...(markRef === undefined ? {} : { markRef })} size={78} />
      <AnimatePresence>
        {slow && <m.p animate={{ opacity: 1, transform: 'translateY(0px)' }} aria-hidden="true" className="acc-restoring__words"
          {...(still ? {} : { exit: { opacity: 0 } })} initial={still ? false : { opacity: 0, transform: 'translateY(6px)' }} transition={SPRING.soft}>
          Confirmando tu sesión
        </m.p>}
      </AnimatePresence>
    </section>
  );
}

/** A primary action whose label becomes «Comprobando…» in place while the Service answers. */
function useCheck(action: () => Promise<unknown> | void) {
  const [checking, setChecking] = useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useDoorChecking(checking);
  const run = () => {
    if (checking) return;
    setChecking(true);
    void Promise.resolve(action()).finally(() => { if (mounted.current) setChecking(false); });
  };
  return { checking, run };
}

function CheckButton({ label, checking, onClick }: { readonly label: string; readonly checking: boolean; readonly onClick: () => void }) {
  return (
    <button className="acc-submit lv-btn lv-btn--primary lv-press" data-busy={checking || undefined} disabled={checking} onClick={onClick} type="button">
      <ActionLabel text={checking ? 'Comprobando…' : label} />
    </button>
  );
}

const UNASSIGNED_TITLE = 'Todavía no tienes un puesto asignado';
const UNASSIGNED_BODY = 'Pide a tu responsable que te asigne a Servicio, Cocina o un departamento en el Constructor. En cuanto lo haga, vuelve a comprobarlo aquí.';

/** The pre-shell answer for a member Identity reports as unplaced: the explanation, a re-check and logout. */
export function AccesoUnassigned({ onRecheck, onLogout }: {
  readonly onRecheck: () => Promise<unknown> | void;
  readonly onLogout: () => void;
}) {
  const { checking, run } = useCheck(onRecheck);
  return (
    <AccesoDoor view="unassigned">
      <section aria-labelledby="acc-unassigned-title" className="acc-state runtime-unassigned" data-runtime-unassigned="">
        <Row index={1}><h1 className="acc-title acc-title--state" id="acc-unassigned-title">{UNASSIGNED_TITLE}</h1></Row>
        <Row index={2}><p className="acc-state__body">{UNASSIGNED_BODY}</p></Row>
        <Row className="acc-actions" index={3}>
          <CheckButton checking={checking} label="Volver a comprobar" onClick={run} />
          <button className="lv-linkbtn lv-press acc-actions__out" disabled={checking} onClick={onLogout} type="button">Cerrar sesión</button>
        </Row>
      </section>
    </AccesoDoor>
  );
}

/**
 * «No se ha podido abrir el Panel»: the Service's sentence as the band (it stays while «Reintentar» says
 * «Comprobando…»), the retry and «Cerrar sesión».
 */
export function AccesoCannotOpen({ notice, failure, retry, onRetry, onLogout }: {
  /** An info band above the failure («Tu sesión ya no requiere cambiar el PIN…»). */
  readonly notice?: ReactNode;
  /** The failure band, inside `.runtime-failure[data-runtime-error]`. */
  readonly failure: ReactNode;
  readonly retry: boolean;
  readonly onRetry: () => Promise<unknown> | void;
  readonly onLogout: () => void;
}) {
  const { checking, run } = useCheck(onRetry);
  return (
    <AccesoDoor view="cannot-open">
      <section aria-labelledby="acc-cannot-title" className="acc-state">
        <Row index={1}><h1 className="acc-title acc-title--state" id="acc-cannot-title">No se ha podido abrir el Panel</h1></Row>
        {notice !== undefined && notice !== null && notice !== false && <Row index={2}>{notice}</Row>}
        {failure}
        <Row className="acc-actions" index={3}>
          {retry && <CheckButton checking={checking} label="Reintentar" onClick={run} />}
          <button className="lv-linkbtn lv-press acc-actions__out" disabled={checking} onClick={onLogout} type="button">Cerrar sesión</button>
        </Row>
      </section>
    </AccesoDoor>
  );
}
